import React, { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import _ from "lodash";
import { Table } from "./table";
import { toJS } from "mobx";
import { safe_get } from "../../../report-visuals";
import { tConvert, useStore } from "../../../helpers";
import { TableHeader } from "./survey-summary";
import { rowHasAudio } from "./row-has-audio";
import { surveyorDisplayName } from "./surveyor-display-name";
import { useTranslation } from "react-i18next";

const AllSurveys = observer((props) => {
    const { surveyResponseStore, projectStore, flexiStore } = useStore();
    const { allSurveyResponses } = surveyResponseStore;

    const data = toJS(allSurveyResponses) || [];
    const [selectedSurveyId, setSelectedSurveyId] = useState(surveyResponseStore.activeSurveyResponseId);
    const [t] = useTranslation(["analytical_summary", "survey_management"]);

    const getValue = (row: any, key: string, defaultValue: any = "") => {
        return safe_get(
            row,
            key,
            safe_get(
                row,
                `meta.${key}`,
                safe_get(row, `qa.${key}`, safe_get(row, `hierarchy.${key}`, defaultValue))
            )
        );
    };

    const columns = useMemo(
        () => [
            {
                Header: t("summary"),
                accessor: (row) => getValue(row, "hashighlights"),
                Cell: (props) => {
                    const row = props.row.original;

                    const hasaudiofile = rowHasAudio(row);
                    const hashighlights = getValue(row, "hashighlights", false);

                    const lat = getValue(row, "lat", "");
                    const long = getValue(row, "long", "");

                    const hasCoordinates = !isNaN(parseFloat(lat)) && !isNaN(parseFloat(long));

                    const ai_flags = safe_get(row, "ai_flags", []);
                    let location_flag = {};

                    try {
                        if (ai_flags.length > 0) {
                            location_flag = _.find(ai_flags, (flag) => flag.flag_type === "red");
                        }
                    } catch (e) {
                        console.log(e);
                    }

                    const is_red_flag = safe_get(location_flag, "flag_type", "");
                    const title = safe_get(location_flag, "reason", "");

                    return (
                        <div className="flex flex-row">
                            {hashighlights && (
                                <i
                                    className="fa-solid fa-star text-2xl text-yellow-300"
                                    title="Contains Important Highlights"
                                />
                            )}

                            {!hasaudiofile && (
                                <i
                                    className="fa-solid fa-microphone-slash text-2xl text-red-300"
                                    title="Audio not yet synced"
                                />
                            )}

                            {!hasCoordinates && (
                                <div title="No Location">
                                    <img src="/images/map-marker-slash.png" style={{ width: 30 }} />
                                </div>
                            )}

                            {is_red_flag === "red" && (
                                <i
                                    className="fa-solid fa-flag-pennant px-2 text-xl text-red-600"
                                    title={title}
                                />
                            )}
                        </div>
                    );
                },
            },
            {
                Header: t("surveyor_name"),
                id: "surveyor_name",
                accessor: (row) =>
                    surveyorDisplayName(row, toJS(surveyResponseStore.allSurveyResponsesSummary)),
            },
            {
                Header: t("survey_id"),
                accessor: "id",
                Cell: (props) => {
                    const value = props.cell.value;
                    const bg_color = value === toJS(surveyResponseStore.activeSurveyResponseId) ? "" : "";

                    return <div className={`flex flex-row items-center ${bg_color}`}>{value}</div>;
                },
            },
            {
                Header: t("date"),
                accessor: (row) => getValue(row, "dt_month_date"),
            },
            {
                Header: t("start_time"),
                accessor: (row) => getValue(row, "dt_start_time"),
                Cell: (props) => {
                    return <div>{tConvert(props.cell.value)}</div>;
                },
            },
            {
                Header: t("end_time"),
                accessor: (row) => getValue(row, "dt_end_time"),
                Cell: (props) => {
                    return <div>{tConvert(props.cell.value)}</div>;
                },
            },
            {
                Header: t("duration_sec"),
                accessor: (row) => getValue(row, "dt_duration_in_seconds", 0),
                Cell: (props) => {
                    const value = props.cell.value || 0;

                    return (
                        <div>
                            {Math.floor(value / 60)}:{String(value % 60).padStart(2, "0")}
                        </div>
                    );
                },
            },
            {
                Header: t("qa_status"),
                accessor: (row) => getValue(row, "qa_status"),
                Cell: (props) => {
                    const value = props.cell.value;

                    return (
                        <div>
                            {value === "valid" && (
                                <i
                                    className="fa-solid fa-check-circle text-2xl text-green-600"
                                    title="valid"
                                />
                            )}

                            {value === "invalid" && (
                                <i
                                    className="fa-solid fa-exclamation-circle text-2xl text-red-600"
                                    title="invalid"
                                />
                            )}

                            {value === "not-checked" && (
                                <i
                                    className="fa-solid fa-minus-circle text-2xl text-yellow-500"
                                    title="not-checked"
                                />
                            )}

                            {value === "onhold" && (
                                <i
                                    className="fa-solid fa-pause-circle text-2xl text-indigo-500"
                                    title="onhold"
                                />
                            )}
                        </div>
                    );
                },
            },
        ],
        [allSurveyResponses, surveyResponseStore.activeSurveyResponseId, surveyResponseStore.allSurveyResponsesSummary]
    );

    useEffect(() => {
        const _path = projectStore.getProjectProperty("ac_map", "");
        flexiStore.load_s3_json_resource("urn-fsd", _path, "");
    }, []);

    if (_.isEmpty(allSurveyResponses)) return <div>{t("no_data_found")}</div>;

    // const row_click_handler = (x) => {
    //     surveyResponseStore.setActiveSurveyResponseId(x.id);
    //     setSelectedSurveyId(x.id);
    // };

    const row_click_handler = async (row) => {
        surveyResponseStore.setActiveSurveyResponseId(row.id);

        console.log("Loading survey:", row.id);

        await surveyResponseStore.getActiveSurveyResponse();

        setSelectedSurveyId(row.id);
    };

    return (
        <div className="all_surveys my-4 flex flex-col">
            <TableHeader title="" />
            <Table
                data={data}
                columns={columns}
                pagesize={20}
                onRowClick={row_click_handler}
                hideFooter={true}
                highlightRow={true}
                activeSurveyResponseId={selectedSurveyId}
            />
        </div>
    );
});
export default AllSurveys;
